import { supabase } from '@/lib/supabase';

export class StorageError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: any
  ) {
    super(message);
    this.name = 'StorageError';
  }
}

export const storageService = {
  uploadLogo: async (file: File) => {
    try {
      // Check authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new StorageError('Authentication required', 'AUTH_ERROR');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (!fileExt) {
        throw new StorageError('Invalid file extension', 'INVALID_FILE');
      }

      const timestamp = new Date().getTime();
      const randomString = Math.random().toString(36).substring(2, 15);
      const fileName = `${timestamp}-${randomString}.${fileExt}`;

      // Upload to 'logos' bucket
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(`public/${fileName}`, file, {
          cacheControl: '3600',
          contentType: file.type,
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new StorageError(
          `Failed to upload file: ${uploadError.message}`,
          'UPLOAD_ERROR',
          uploadError
        );
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('logos')
        .getPublicUrl(`public/${fileName}`);

      return {
        url: urlData.publicUrl,
        filename: fileName
      };
    } catch (error) {
      console.error('Storage error:', error);
      if (error instanceof StorageError) {
        throw error;
      }
      throw new StorageError(
        'Failed to upload file',
        'UPLOAD_ERROR',
        error
      );
    }
  },

  deleteLogo: async (filename: string) => {
    try {
      // Check authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new StorageError('Authentication required', 'AUTH_ERROR');
      }

      const { error } = await supabase.storage
        .from('logos')
        .remove([`public/${filename}`]);

      if (error) {
        throw new StorageError(
          `Failed to delete file: ${error.message}`,
          'DELETE_ERROR',
          error
        );
      }
    } catch (error) {
      console.error('Delete error:', error);
      if (error instanceof StorageError) {
        throw error;
      }
      throw new StorageError(
        'Failed to delete file',
        'DELETE_ERROR',
        error
      );
    }
  }
}; 